<script setup lang="ts">
import { computed } from "vue";
import { useTranslation } from "i18next-vue";
import DisplayOptionRow from "./DisplayOptionRow.vue";
import DisplaySectionLabel from "./DisplaySectionLabel.vue";
import DisplayToolbarOptions from "./DisplayToolbarOptions.vue";
import { useDisplaySettings } from "@/composable/useDisplaySettings";
import { CANVAS_UI_SIZES } from "@/stores/Settings";
import { RULER_SIZES } from "@/drawing/overlay/rulerSizes";
import { PLAN_BACKGROUNDS } from "@/utils/planBackground";
import { isApplyingPlanBackground } from "@/stores/PlanBackgroundState";

/**
 * Colonne de gauche : les réglages d'affichage de l'interface, du plan et de
 * la barre d'outils (la colonne de droite liste ses outils).
 */
const { t } = useTranslation();
const {
  canvasIconSize,
  canvasTextSize,
  planBackground,
  handlesScale,
  rulerThickness,
  toolHintsOn,
} = useDisplaySettings();

const planBackgroundOptions = computed(() =>
  PLAN_BACKGROUNDS.map((name) => ({
    value: name,
    label: t(`canvas:display.planBackground.${name}`),
  })),
);

const rulerSizeOptions = computed(() =>
  RULER_SIZES.map((size) => ({
    value: size,
    label: t(`canvas:display.rulerSize.${size}`),
  })),
);

const uiSizeOptions = computed(() =>
  CANVAS_UI_SIZES.map((size) => ({
    value: size,
    label: t(`canvas:display.uiSizeShort.${size}`),
  })),
);
</script>

<template>
  <div>
    <DisplaySectionLabel>{{
      $t("canvas:display.sections.interface")
    }}</DisplaySectionLabel>
    <DisplayOptionRow
      :title="$t('canvas:display.iconSize.title')"
      :description="$t('canvas:display.iconSize.desc')"
    >
      <el-segmented
        v-model="canvasIconSize"
        :options="uiSizeOptions"
        size="small"
        class="display-segmented"
        :aria-label="$t('canvas:display.iconSize.title')"
      />
    </DisplayOptionRow>
    <DisplayOptionRow
      :title="$t('canvas:display.textSize.title')"
      :description="$t('canvas:display.textSize.desc')"
    >
      <el-segmented
        v-model="canvasTextSize"
        :options="uiSizeOptions"
        size="small"
        class="display-segmented"
        :aria-label="$t('canvas:display.textSize.title')"
      />
    </DisplayOptionRow>

    <DisplaySectionLabel>{{
      $t("canvas:display.sections.drawing")
    }}</DisplaySectionLabel>
    <DisplayOptionRow
      :title="$t('canvas:display.planBackground.title')"
      :description="$t('canvas:display.planBackground.desc')"
    >
      <el-segmented
        v-model="planBackground"
        :options="planBackgroundOptions"
        :disabled="isApplyingPlanBackground"
        size="small"
        class="display-segmented"
        :aria-label="$t('canvas:display.planBackground.title')"
      />
    </DisplayOptionRow>
    <DisplayOptionRow
      :title="$t('canvas:settings.handlesSize.option.type.title')"
      :description="$t('canvas:settings.handlesSize.option.type.desc')"
    >
      <el-slider
        v-model="handlesScale"
        :step="10"
        :show-tooltip="false"
        class="display-slider"
        :aria-label="$t('canvas:settings.handlesSize.option.type.title')"
      />
    </DisplayOptionRow>
    <DisplayOptionRow
      :title="$t('canvas:display.rulerSize.title')"
      :description="$t('canvas:display.rulerSize.desc')"
    >
      <el-segmented
        v-model="rulerThickness"
        :options="rulerSizeOptions"
        size="small"
        class="display-segmented"
        :aria-label="$t('canvas:display.rulerSize.title')"
      />
    </DisplayOptionRow>
    <DisplayOptionRow
      :title="$t('canvas:settings.toolHints.option.title')"
      :description="$t('canvas:settings.toolHints.option.desc')"
    >
      <el-switch
        v-model="toolHintsOn"
        class="display-switch"
        :aria-label="$t('canvas:settings.toolHints.option.title')"
      />
    </DisplayOptionRow>

    <DisplayToolbarOptions />
  </div>
</template>

<style lang="scss" scoped>
.display-slider {
  flex-shrink: 0;
  width: 120px;
  margin-right: 6px;
}
</style>
